import { AddressValidationPayload } from '../refactoredComponents/interface/utilsInterface/validateAddressInterface';

type ResolvedCacheEntry<T> = {
  status: 'resolved';
  response: T;
};

type PendingCacheEntry<T> = {
  status: 'pending';
  promise: Promise<T>;
};

type CacheEntry<T> = ResolvedCacheEntry<T> | PendingCacheEntry<T>;

const cache = new Map<string, CacheEntry<unknown>>();

function getSessionStoreNumber(): string | null {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }

  try {
    return window.sessionStorage.getItem('storeNumber');
  } catch {
    return null;
  }
}

export function normalizeField(value: string | undefined | null): string {
  if (!value) {
    return '';
  }
  return value.trim().replace(/\s+/g, ' ');
}

function normalizeStreetAddress(value: string | undefined | null): string {
  return normalizeField(value).replace(/\s*,\s*/g, ', ');
}

function buildFingerprint(
  parts: string[],
  storeNumber?: string | null
): string | null {
  const normalizedStore = normalizeField(storeNumber);
  const normalizedParts = [...parts.map(normalizeField), normalizedStore];

  if (normalizedParts.every((part) => part.length === 0)) {
    return null;
  }

  return normalizedParts.join('|').toLowerCase();
}

export function extractFingerprint(
  payload: AddressValidationPayload
): string | null {
  const parts: string[] = [];
  const googlePayload = payload.validateAddress;

  if (googlePayload) {
    if (Array.isArray(googlePayload.addressLines)) {
      parts.push(...googlePayload.addressLines);
    }
    parts.push(googlePayload.postalCode || '');
    parts.push(googlePayload.regionCode || '');
  }

  if (Array.isArray(payload.Addresses) && payload.Addresses.length > 0) {
    const address = payload.Addresses[0];
    parts.push(address.StreetAddress || '');
    parts.push(address.City || '');
    parts.push(address.StateProvince || '');
    parts.push(address.PostalCode || '');
    parts.push(address.Country || '');
  }

  const locationNumber =
    payload.LocationNumber || getSessionStoreNumber() || '';
  return buildFingerprint(parts, locationNumber);
}

export function normalizePayload(
  payload: AddressValidationPayload
): AddressValidationPayload {
  const normalizedPayload: AddressValidationPayload = JSON.parse(
    JSON.stringify(payload)
  );

  if (normalizedPayload.validateAddress) {
    normalizedPayload.validateAddress.postalCode = normalizeField(
      normalizedPayload.validateAddress.postalCode
    );
    normalizedPayload.validateAddress.regionCode = normalizeField(
      normalizedPayload.validateAddress.regionCode
    );
    if (Array.isArray(normalizedPayload.validateAddress.addressLines)) {
      normalizedPayload.validateAddress.addressLines =
        normalizedPayload.validateAddress.addressLines.map((line) =>
          normalizeField(line)
        );
    }
  }

  if (Array.isArray(normalizedPayload.Addresses)) {
    normalizedPayload.Addresses = normalizedPayload.Addresses.map(
      (address) => ({
        ...address,
        StreetAddress: normalizeStreetAddress(address.StreetAddress),
        City: normalizeField(address.City),
        StateProvince: normalizeField(address.StateProvince),
        PostalCode: normalizeField(address.PostalCode),
        Country: normalizeField(address.Country),
      })
    );
  }

  return normalizedPayload;
}

function getCachedEntryValue<T>(
  cachedEntry: CacheEntry<unknown>
): Promise<T> | T {
  if (cachedEntry.status === 'pending') {
    return cachedEntry.promise as Promise<T>;
  }

  return cachedEntry.response as T;
}

function canCacheResponse<T>(response: T): boolean {
  const status = (response as { status?: number } | null)?.status;

  return !(
    response == null ||
    (status !== undefined && (status < 200 || status >= 300))
  );
}

export function withAddressCache<T>(
  apiFn: (payload: AddressValidationPayload) => Promise<T>
): (payload: AddressValidationPayload) => Promise<T> {
  return async (payload: AddressValidationPayload): Promise<T> => {
    if (payload.enableGoogleAddressValidation !== true) {
      return apiFn(payload);
    }

    const normalizedPayload = normalizePayload(payload);
    const fingerprint = extractFingerprint(normalizedPayload);

    if (!fingerprint) {
      return apiFn(normalizedPayload);
    }

    const cachedEntry = cache.get(fingerprint);
    if (cachedEntry) {
      const cachedEntryValue = getCachedEntryValue<T>(cachedEntry);
      if (cachedEntryValue !== undefined) {
        return cachedEntryValue;
      }
    }

    const request = apiFn(normalizedPayload).then(
      (response) => {
        if (!canCacheResponse(response)) {
          cache.delete(fingerprint);
          return response;
        }
        cache.set(fingerprint, {
          status: 'resolved',
          response,
        });
        return response;
      },
      (error) => {
        cache.delete(fingerprint);
        throw error;
      }
    );

    cache.set(fingerprint, {
      status: 'pending',
      promise: request,
    });

    return request;
  };
}
